/**
 * Shared helpers for displaying workflow validation errors across
 * NotificationPanel, ValidationErrorsPanel, and TriggerWorkflowDialog.
 */

import type { ValidationError } from '@/services/workflowValidationService';

/** Validator categories that are noisy implementation details, not user-facing. */
const INTERNAL_VALIDATION_ERROR_TYPES = new Set(['AzureApi', 'Schema', 'JsonParse']);

/**
 * Format a validation error `type` for display. Returns `null` when the type
 * is an internal-only category that shouldn't be surfaced.
 */
export function formatValidationErrorType(type: string | undefined): string | null {
  if (!type || INTERNAL_VALIDATION_ERROR_TYPES.has(type)) return null;
  return type;
}

/**
 * A validation error annotated with the node it belongs to (omitted for
 * workflow-level / global errors). Shared by the panel (click payload) and
 * the trigger dialog (warning list).
 */
export interface FlatValidationError {
  /** Node name when the error is attributable to a specific node. */
  nodeName?: string;
  error: ValidationError;
}

/**
 * Flatten the validation store's `{ nodeErrors, globalErrors }` shape into a
 * single array. Global errors appear first, then per-node errors sorted
 * alphabetically by node name for stable rendering.
 */
export function flattenValidationErrors(
  nodeErrors: Record<string, ValidationError[]>,
  globalErrors: ValidationError[],
): FlatValidationError[] {
  const globals: FlatValidationError[] = globalErrors.map((error) => ({ error }));
  const grouped: FlatValidationError[] = Object.keys(nodeErrors)
    .sort((a, b) => a.localeCompare(b))
    .flatMap((nodeName) =>
      (nodeErrors[nodeName] ?? []).map((error) => ({ nodeName, error })),
    );
  return [...globals, ...grouped];
}
